/**
 * UI xác nhận shipment Shopee: gọi shipping-parameter → chọn preset (không bắt dán tay).
 * Parse căn chỉnh backend _getIntegratedShipParams (order-status-sync-service.js).
 */
import Swal from 'sweetalert2';
import {
  getManualShipmentSwalHtml,
  attachManualShipmentSwalListeners,
  parseManualShipmentPreConfirm,
  MANUAL_SHIP_NONE,
  SHIP_UI_MODE_PRESET,
} from '@/utils/shopeeManualShipment';

function truncate(s, max) {
  const t = String(s || '');
  if (t.length <= max) return t;
  return `${t.slice(0, max)}…`;
}

function rawPackageList(order = {}) {
  const raw = order.rawOrderData;
  const list = raw?.package_list ?? raw?.packageList;
  return Array.isArray(list) ? list : [];
}

/**
 * Đơn chưa tách gói (0–1 package_list): ship_order của Shopee không cho kèm package_number
 * (“Please don't request with package_number for this unsplit order”) — hay gặp đơn hỏa tốc / instant.
 */
export function isShopeeUnsplitPackageOrder(order = {}) {
  return rawPackageList(order).length <= 1;
}

/**
 * Mã gói thật từ dữ liệu đơn (OFG…) — chỉ mang ý nghĩa khi đơn đã split nhiều gói; với đơn 1 gói chỉ để hiển thị / in,
 * không dùng cho ship_order.
 */
export function resolveShopeeOrderPackageIdentifier(order = {}) {
  if (order.packageNumber !== undefined && order.packageNumber !== null) {
    const s = String(order.packageNumber).trim();
    if (s !== '') return s;
  }
  const list = rawPackageList(order);
  for (const pkg of list) {
    const n = pkg?.package_number ?? pkg?.packageNumber;
    if (n != null && String(n).trim() !== '') return String(n);
  }
  return undefined;
}

/**
 * Chỉ đơn split nhiều gói mới kèm packageNumber lên proxy ship / create-shipment / shipping-parameter.
 */
export function resolveShopeePackageNumberForShipApi(order = {}) {
  if (isShopeeUnsplitPackageOrder(order)) {
    return undefined;
  }
  return resolveShopeeOrderPackageIdentifier(order);
}

/** Shopee từ chối ship_order với ship_type dropoff cho các kênh này (SPX Instant VN — 50022). */
const SHOPEE_LOGISTICS_CHANNELS_PICKUP_ONLY = new Set([50022]);

export function resolvePrimaryLogisticsChannelIdFromOrder(order = {}) {
  const list = rawPackageList(order);
  if (list.length) {
    const fromPkg = list[0]?.logistics_channel_id ?? list[0]?.logisticsChannelId;
    if (fromPkg != null && String(fromPkg).trim() !== '') {
      const n = Number(fromPkg);
      if (!Number.isNaN(n)) return n;
    }
  }
  const o = order?.logisticsChannelId ?? order?.logistics_channel_id;
  if (o != null && String(o).trim() !== '') {
    const n = Number(o);
    if (!Number.isNaN(n)) return n;
  }
  return NaN;
}

export function orderRequiresPickupNotDropoff(order = {}) {
  const id = resolvePrimaryLogisticsChannelIdFromOrder(order);
  return !Number.isNaN(id) && SHOPEE_LOGISTICS_CHANNELS_PICKUP_ONLY.has(id);
}

function firstRawPackageEntry(order = {}) {
  const list = rawPackageList(order);
  if (!list.length) return null;
  return list[0];
}

/**
 * Khi get_shipping_parameter không có preset: chỉ synthesize dropoff nếu kênh cho phép.
 * SPX Instant (50022): không dùng dropoff — cần pickup từ API / Seller Centre.
 * @returns {{ shipType: 'dropoff'; label: string; dropoff: { logistics_channel_id: number } } | null}
 */
export function buildSyntheticDropoffChoiceFromPackageList(order = {}) {
  const pkg = firstRawPackageEntry(order);
  if (!pkg) return null;
  const channelId = pkg.logistics_channel_id ?? pkg.logisticsChannelId;
  if (channelId == null || String(channelId).trim() === '') return null;
  const nid = Number(channelId);
  if (!Number.isNaN(nid) && SHOPEE_LOGISTICS_CHANNELS_PICKUP_ONLY.has(nid)) {
    return null;
  }
  const pn = pkg.package_number ?? pkg.packageNumber;
  return {
    shipType: 'dropoff',
    label: pn
      ? `Gửi tại điểm — gói ${pn}, kênh #${channelId}`
      : `Gửi tại điểm — kênh logistics #${channelId}`,
    dropoff: { logistics_channel_id: Number(channelId) },
  };
}

/**
 * Thêm preset dropoff từ package_list nếu API không trả lựa chọn nào (tránh gửi create-shipment thiếu shipType).
 * @param {object} order
 * @param {Array} choices
 */
export function mergeShippingChoicesWithPackageFallback(order, choices) {
  const base = Array.isArray(choices) ? [...choices] : [];
  if (base.length) return base;
  const syn = buildSyntheticDropoffChoiceFromPackageList(order);
  return syn ? [syn] : [];
}

/** Payload dropoff chỉ khi không thuộc kênh bắt buộc pickup (SPX Instant). */
export function getDropoffShipmentPayloadFromOrderPackageList(order) {
  const syn = buildSyntheticDropoffChoiceFromPackageList(order);
  if (!syn?.dropoff) return null;
  return { shipType: 'dropoff', dropoff: syn.dropoff };
}

/**
 * SAU derivePresetShipChoices: fallback dropoff + lọc bỏ dropoff cho đơn kênh 50022; pickup lên đầu.
 */
export function finalizeShipChoices(order, choicesFromShippingParameterApi) {
  let arr = mergeShippingChoicesWithPackageFallback(order, choicesFromShippingParameterApi || []);
  if (orderRequiresPickupNotDropoff(order)) {
    arr = arr.filter((c) => c && c.shipType !== 'dropoff');
  }
  const pickups = arr.filter((c) => c && c.shipType === 'pickup');
  const rest = arr.filter((c) => !c || c.shipType !== 'pickup');
  return [...pickups, ...rest];
}

function dedupeChoices(choices) {
  const seen = new Set();
  const out = [];
  for (const c of choices) {
    const key = `${c.shipType}::${JSON.stringify(c.pickup || c.dropoff || {})}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(c);
  }
  return out;
}

function formatPickupLabel(addressId, addrText, timePart) {
  const a = truncate(addrText, 40);
  const idPart = addressId != null ? `#${addressId}` : '';
  return `Lấy hàng (${idPart})${a ? ` — ${a}` : ''} — ${timePart}`;
}

/**
 * Trả các lựa chọn ship (preset) có thể gửi vào create-shipment — mirror logic backend.
 * @param {object} raw - body `data` từ GET .../shipping-parameter (response Shopee get_shipping_parameter)
 * @returns {Array<{ shipType: 'pickup'|'dropoff'; label: string; pickup?: object; dropoff?: object }>}
 */
export function derivePresetShipChoices(raw) {
  const choices = [];
  if (!raw || typeof raw !== 'object') return choices;

  const info = raw.info !== undefined ? raw.info : raw;
  const respRoot = raw;

  // --- Pickup format 1 (VN): address_list x time_slot_list
  const pickup = info.pickup || respRoot.pickup;
  if (pickup) {
    const addrList = pickup.address_list || pickup.address_list_list || [];
    const logistics = pickup.logistics || [];
    const defaultChannelId =
      logistics[0]?.logistics_channel_id ?? logistics[0]?.logisticsChannelId;

    for (const addr of addrList) {
      const addressId = addr.address_id ?? addr.addressId;
      const addrText = String(addr.address ?? '');
      const timeSlots =
        addr.time_slot_list || addr.pickup_time_id_list || addr.pickupTimeIdList || [];

      if (!timeSlots.length) {
        const pickupTimeId = addr.pickup_time_id ?? addr.pickupTimeId;
        if (addressId != null && pickupTimeId != null && String(pickupTimeId) !== '') {
          const pickupBody = {
            address_id: Number(addressId),
            pickup_time_id: String(pickupTimeId),
            address: addrText,
          };
          if (defaultChannelId != null) {
            pickupBody.logistics_channel_id = Number(defaultChannelId);
          }
          choices.push({
            shipType: 'pickup',
            label: formatPickupLabel(addressId, addrText, pickupTimeId),
            pickup: pickupBody,
          });
        }
        continue;
      }

      for (const slot of timeSlots) {
        const pickupTimeId = slot.pickup_time_id ?? slot.pickupTimeId;
        if (addressId == null || pickupTimeId == null || String(pickupTimeId) === '') {
          continue;
        }
        const pickupBody = {
          address_id: Number(addressId),
          pickup_time_id: String(pickupTimeId),
          address: addrText,
        };
        if (defaultChannelId != null) {
          pickupBody.logistics_channel_id = Number(defaultChannelId);
        }
        const timeLabel =
          slot.time_text || slot.text || slot.remark || String(pickupTimeId);
        choices.push({
          shipType: 'pickup',
          label: formatPickupLabel(addressId, addrText, timeLabel),
          pickup: pickupBody,
        });
      }
    }
  }

  const addresses = info.addresses || respRoot.addresses || [];
  const logisticsAll = info.logistics || respRoot.logistics || [];
  const pickupLog = logisticsAll.find(
    (l) =>
      String(l.logisticsDeliveryType || l.logistics_delivery_type || '').toUpperCase() ===
      'PICK_UP',
  );
  const dropoffLog = logisticsAll.find(
    (l) =>
      String(l.logisticsDeliveryType || l.logistics_delivery_type || '').toUpperCase() ===
      'DROP_OFF',
  );


  // --- Pickup format 2 (Ginee-style) — chỉ khi format 1 không cho lựa chọn pickup
  if (addresses.length > 0 && pickupLog && !choices.some((c) => c.shipType === 'pickup')) {
    const detailList =
      pickupLog.logisticDetailList || pickupLog.logistic_detail_list || [];
    const providerId =
      detailList[0]?.logisticsProviderId ?? detailList[0]?.logistics_provider_id;
    const addr = addresses[0];
    const pickupTimeList = addr.pickupTimeList || addr.pickup_time_list || [];
    const pickupTimeId = pickupTimeList[0]?.pickupTimeId ?? pickupTimeList[0]?.pickup_time_id;
    const addressId = addr.addressId ?? addr.address_id;
    if (addressId != null && providerId != null) {
      const pickupBody = {
        logistics_channel_id: Number(providerId),
        address_id: String(addressId),
        address: String(addr.address ?? ''),
      };
      if (pickupTimeId) {
        pickupBody.pickup_time_id = String(pickupTimeId);
      }
      choices.push({
        shipType: 'pickup',
        label: `Lấy hàng (kênh ${providerId}): ${truncate(addr.address || '', 48)}`,
        pickup: pickupBody,
      });
    }
  }

  // --- Dropoff format 1
  const dropoff = info.dropoff || respRoot.dropoff;
  if (dropoff) {
    const list =
      dropoff.logistics_channel_list ||
      dropoff.logisticsChannelList ||
      dropoff.logistics ||
      [];
    for (const ch of list) {
      const channelId =
        ch?.logistics_channel_id ??
        ch?.logisticsChannelId ??
        (typeof ch === 'number' ? ch : null);
      if (channelId == null) continue;
      const name = ch.preferred_delivery_name || ch.name || `Kênh ${channelId}`;
      choices.push({
        shipType: 'dropoff',
        label: `Gửi tại điểm: ${name} (#${channelId})`,
        dropoff: { logistics_channel_id: Number(channelId) },
      });
    }
  }

  // --- Dropoff format 2
  if (dropoffLog) {
    const dropList =
      dropoffLog.logisticDetailList || dropoffLog.logistic_detail_list || [];
    const chId =
      dropList[0]?.logisticsProviderId ?? dropList[0]?.logistics_provider_id;
    if (chId != null) {
      choices.push({
        shipType: 'dropoff',
        label: `Gửi tại điểm (kênh logistics #${chId})`,
        dropoff: { logistics_channel_id: Number(chId) },
      });
    }
  }

  return dedupeChoices(choices);
}

function escapeHtmlFe(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * HTML lớp ngoài + select preset + phần nâng cao (dán tay).
 * @param {'create'|'confirm'|null|undefined} [fixedShipmentApiMode] — khi set: ẩn dropdown luồng API, cố định create-shipment vs confirm-ship-order.
 */
export function getShipmentConfirmSwalHtml({
  snSafe,
  choices = [],
  parameterError = null,
  spxPickupRequired = false,
  fixedShipmentApiMode = null,
}) {
  const errSafe = parameterError != null ? escapeHtmlFe(parameterError) : null;
  const presetOptions = choices
    .map(
      (c, i) =>
        `<option value="preset:${i}">${String(c.label || `Phương án ${i + 1}`).replace(
          /</g,
          '&lt;',
        )}</option>`,
    )
    .join('');

  const spxPickupAlert = spxPickupRequired
    ? [
        '<div class="alert alert-info py-2 px-3 small mb-3 border-info">',
        'Đơn <strong>SPX Instant</strong>: Shopee chỉ nhận <strong>pickup</strong> (shipper đến lấy). ',
        'Không dùng <strong>dropoff</strong>. Nếu không có preset pickup, chọn <strong>Nâng cao</strong> → Pickup ',
        'và dán JSON đúng từ Seller Centre / phiên bản ',
        '<code class="small">get_shipping_parameter</code>.',
        '</div>',
      ].join('')
    : '';

  const fixedMode =
    fixedShipmentApiMode === 'create' || fixedShipmentApiMode === 'confirm'
      ? fixedShipmentApiMode
      : null;

  const apiFlowBlock = fixedMode
    ? [
        `<input type="hidden" id="swal-ship-api-mode-fixed" value="${fixedMode}" />`,
        '<div class="alert alert-secondary py-2 px-3 small mb-3 mb-md-4 border-secondary">',
        fixedMode === 'create'
          ? 'Luồng cố định: <strong>POST …/create-shipment</strong> — đầy đủ (tracking / mã vận đơn).'
          : 'Luồng cố định: <strong>POST …/confirm-ship-order</strong> — chỉ ship_order (có thể PROCESSED trước khi có mã).',
        '</div>',
      ].join('')
    : [
        '<div class="form-group mb-3">',
        '<label for="swal-ship-api-mode" class="font-weight-bolder font-size-sm text-dark mb-2 d-block">',
        'Luồng API (Ship order / logistics)',
        '</label>',
        '<select id="swal-ship-api-mode" class="form-control form-control-sm">',
        '<option value="create" selected>',
        'Đầy đủ — ưu tiên tracking / cập nhật mã vận đơn (POST …/create-shipment)',
        '</option>',
        '<option value="confirm">',
        'Chỉ xác nhận ship_order — trạng thái có thể PROCESSED, chưa cần mã (POST …/confirm-ship-order)',
        '</option>',
        '</select>',
        '<small class="form-text text-muted d-block mt-1">',
        'Theo tài liệu Shopee / RFC nội bộ: READY_TO_SHIP (và RETRY_SHIP) gọi <code class="small">ship_order</code>. ',
        'Chỉ cần hoàn tất bước tổ chức giao trên sàn mà không cần in nhãn ngay → chọn «Chỉ xác nhận». ',
        'Cần lấy tracking / in phiếu → «Đầy đủ».',
        '</small>',
        '</div>',
      ].join('');

  return `
    <div class="swal-shipment-manual-body text-left">
      <p class="swal-shipment-manual__order-sn mb-2 mb-sm-3">
        Đơn Shopee: <strong class="text-dark">${snSafe}</strong>
      </p>
      <p class="text-muted mb-3 small swal-shipment-manual__intro">
        Hệ thống đã đọc tham số vận chuyển từ Shopee để hiển thị các lựa chọn bên dưới — bạn chỉ cần <strong>chọn một dòng phù hợp</strong>,
        không cần nhập tay. Chọn <strong>Nâng cao</strong> chỉ khi không có phương án đúng hoặc được IT hướng dẫn.
      </p>
      ${apiFlowBlock}
      <div class="border rounded px-3 py-2 mb-3 bg-light">
        <p class="small font-weight-bold mb-2">Xác nhận đơn Shopee (confirm_order)</p>
        <p class="small text-muted mb-2 mb-0">
          <strong>Mặc định không tick:</strong> backend <strong>không</strong> gọi <code class="small">confirm_order</code>
          — chỉ logistics khi đơn đã READY_TO_SHIP / RETRY_SHIP. Tick nếu đơn còn UNPAID / cần một bước «xác nhận + ship» như trước.
        </p>
        <div class="custom-control custom-checkbox mt-2">
          <input type="checkbox" class="custom-control-input" id="swal-ship-confirm-before" />
          <label class="custom-control-label small" for="swal-ship-confirm-before">
            Gọi confirm trước khi ship (<code class="small">confirmOrderBeforeShip</code>)
          </label>
        </div>
        <div class="custom-control custom-checkbox mt-1">
          <input type="checkbox" class="custom-control-input" id="swal-ship-force-confirm" />
          <label class="custom-control-label small" for="swal-ship-force-confirm">
            Ép luôn thử confirm (<code class="small">forceConfirmOrder</code> — cần nhánh confirm được bật)
          </label>
        </div>
      </div>
      ${spxPickupAlert}
      ${errSafe ? `<div class="alert alert-warning py-2 px-3 small mb-3">${errSafe}</div>` : ''}
      ${!errSafe && choices.length > 0 ? `<p class="small text-muted mb-2">Danh sách lấy từ Seller Shopee (get shipping parameter).</p>` : ''}
      ${!errSafe && !choices.length ? `<div class="alert alert-light border py-2 px-3 small mb-3 mb-md-4">Không tách được phương án có sẵn — dùng <strong>Tự động</strong> hoặc mục <strong>Nâng cao</strong> bên dưới.</div>` : ''}

      <div class="form-group mb-3">
        <label for="swal-ship-primary" class="font-weight-bolder font-size-sm text-dark mb-2 d-block">
          Cách tạo vận đơn shipment
        </label>
        <select id="swal-ship-primary" class="form-control form-control-sm">
          <option value="auto">Tự động — không gửi sẵn pickup/dropoff (để backend xử lý)</option>
          ${presetOptions}
          <option value="manual">Nâng cao — tự nhập / dán theo Seller Centre</option>
        </select>
      </div>

      <details id="swal-ship-advanced-details" class="swal-ship-advanced-details mb-0">
        <summary class="font-size-sm mb-3 text-muted" style="cursor: pointer;">
          Thêm tùy chọn nâng cao…
        </summary>
        ${getManualShipmentSwalHtml()}
      </details>
    </div>`;
}

export function attachShipmentConfirmDialogListeners(options = {}) {
  const { preferFirstPreset = false } = options;
  const primary = document.getElementById('swal-ship-primary');
  const details = document.getElementById('swal-ship-advanced-details');
  const syncAdvancedVisibility = () => {
    const v = primary?.value;
    const manual = v === 'manual';
    if (details) {
      details.open = manual || false;
    }
    if (manual) {
      attachManualShipmentSwalListeners();
    }
  };
  if (primary && !primary.dataset.swShipBound) {
    primary.dataset.swShipBound = '1';
    primary.addEventListener('change', syncAdvancedVisibility);
  }
  if (preferFirstPreset && primary) {
    const firstPreset = Array.from(primary.options || []).find((o) =>
      String(o.value || '').startsWith('preset:'),
    );
    if (firstPreset) {
      primary.value = firstPreset.value;
    }
  }
  syncAdvancedVisibility();
}

/** Đọc từ popup xác nhận shipment — gửi kèm create-shipment / confirm-ship-order / shipping-parameter. */
export function readSwalShopeeConfirmOrderOptions() {
  return {
    confirmOrderBeforeShip: !!document.getElementById('swal-ship-confirm-before')?.checked,
    forceConfirmOrder: !!document.getElementById('swal-ship-force-confirm')?.checked,
  };
}

function envelopeShipmentConfirmPayload(payload) {
  if (payload === false) {
    return false;
  }
  return { ...payload, ...readSwalShopeeConfirmOrderOptions() };
}

/**
 * @param {number[]} [_shipChoicesIgnored] không dùng — giữ mở rộng
 * @returns {false | { apiMode: 'create'|'confirm', choice: object, confirmOrderBeforeShip: boolean, forceConfirmOrder: boolean }}
 */
export function parseShipmentConfirmPreConfirm(shipChoices) {
  const fixedEl = document.getElementById('swal-ship-api-mode-fixed');
  let apiMode = 'create';
  if (fixedEl && String(fixedEl.value || '').trim() !== '') {
    apiMode = String(fixedEl.value) === 'confirm' ? 'confirm' : 'create';
  } else {
    const apiModeEl = document.getElementById('swal-ship-api-mode');
    apiMode =
      apiModeEl && String(apiModeEl.value || '') === 'confirm' ? 'confirm' : 'create';
  }

  const primary = document.getElementById('swal-ship-primary');
  if (!primary) {
    const inner = parseManualShipmentPreConfirm();
    if (inner === false) {
      return false;
    }
    return envelopeShipmentConfirmPayload({ apiMode, choice: inner });
  }

  const v = primary.value;
  if (!v || v === 'auto' || v === MANUAL_SHIP_NONE) {
    return envelopeShipmentConfirmPayload({ apiMode, choice: { mode: MANUAL_SHIP_NONE } });
  }
  if (v === 'manual') {
    const inner = parseManualShipmentPreConfirm();
    if (inner === false) {
      return false;
    }
    if (!inner || inner.mode === MANUAL_SHIP_NONE) {
      Swal.showValidationMessage(
        'Chọn Pickup hoặc Dropoff trong phần nâng cao và điền đủ ô bên dưới (hoặc đổi lại thành Tự động / phương án gợi ý ở trên).',
      );
      return false;
    }
    return envelopeShipmentConfirmPayload({ apiMode, choice: inner });
  }
  if (v.startsWith('preset:')) {
    const idx = parseInt(v.split(':')[1], 10);
    const c = Array.isArray(shipChoices) ? shipChoices[idx] : null;
    if (!c || !c.shipType) {
      Swal.showValidationMessage('Phương án không hợp lệ.');
      return false;
    }
    if (c.shipType === 'pickup' && c.pickup) {
      return envelopeShipmentConfirmPayload({
        apiMode,
        choice: {
          mode: SHIP_UI_MODE_PRESET,
          shipType: 'pickup',
          pickup: c.pickup,
        },
      });
    }
    if (c.shipType === 'dropoff' && c.dropoff) {
      return envelopeShipmentConfirmPayload({
        apiMode,
        choice: {
          mode: SHIP_UI_MODE_PRESET,
          shipType: 'dropoff',
          dropoff: c.dropoff,
        },
      });
    }
  }
  Swal.showValidationMessage('Vui lòng chọn một phương án.');
  return false;
}
